import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, FileText, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LegalCaseRecord {
  id: number;
  person_id: number;
  agency_id: number;
  case_details: string | null;
  law_enforcement: string | null;
  created_by: number | null;
  created_at: string;
  author_first_name?: string;
  author_last_name?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface LegalCasesListProps {
  personId: string;
}

export function LegalCasesList({ personId }: LegalCasesListProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingCaseId, setEditingCaseId] = useState<number | null>(null);

  const { data, isLoading } = useQuery<{ data: LegalCaseRecord[] }>({
    queryKey: ["legalCases", personId],
    queryFn: () =>
      api.get<{ data: LegalCaseRecord[] }>(
        `/clients/${personId}/legal-cases`,
      ),
    enabled: !!personId,
  });

  const createMutation = useMutation({
    mutationFn: (body: { caseDetails?: string; lawEnforcement?: string }) =>
      api.post(`/clients/${personId}/legal-cases`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["legalCases", personId] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number;
      caseDetails?: string;
      lawEnforcement?: string;
    }) => api.put(`/clients/${personId}/legal-cases/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["legalCases", personId] });
      setEditingCaseId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      api.delete(`/clients/${personId}/legal-cases/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["legalCases", personId] });
    },
  });

  const cases = data?.data ?? [];

  // Friendly labels for common JSON keys from legacy data
  const fieldLabels: Record<string, string> = {
    case_number: "Case Number",
    case_type: "Case Type",
    court_name: "Court",
    court: "Court",
    judge_name: "Judge",
    judge: "Judge",
    attorney_name: "Attorney",
    attorney: "Attorney",
    prosecutor: "Prosecutor",
    defendant: "Defendant",
    charges: "Charges",
    charge: "Charges",
    offense: "Offense",
    offense_date: "Offense Date",
    filing_date: "Filing Date",
    hearing_date: "Hearing Date",
    next_hearing: "Next Hearing",
    disposition: "Disposition",
    sentence: "Sentence",
    status: "Status",
    notes: "Notes",
    description: "Description",
    details: "Details",
    bond_amount: "Bond Amount",
    bond: "Bond",
    arrest_date: "Arrest Date",
    report_number: "Report Number",
    officer: "Officer",
    agency: "Agency",
    docket_number: "Docket Number",
    county: "County",
    state: "State",
    plea: "Plea",
    outcome: "Outcome",
    victim_advocate: "Victim Advocate",
  };

  function formatFieldLabel(key: string): string {
    return fieldLabels[key.toLowerCase()] ?? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function renderLawEnforcement(raw: string) {
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
        const entries = Object.entries(parsed).filter(([, v]) => v !== null && v !== "" && v !== undefined);
        if (entries.length === 0) return <p>Law Enforcement: {raw}</p>;
        return (
          <div className="space-y-0.5">
            {entries.map(([key, value]) => (
              <p key={key}>
                <span className="font-medium">{formatFieldLabel(key)}:</span>{" "}
                {Array.isArray(value) ? value.map((v) => String(v).replace(/_/g, " ")).join(", ") : String(value)}
              </p>
            ))}
          </div>
        );
      }
      return <p>Law Enforcement: {raw}</p>;
    } catch {
      return <p>Law Enforcement: {raw}</p>;
    }
  }

  function renderCaseDetails(raw: string | null) {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        return <p className="mt-2 text-sm text-foreground whitespace-pre-wrap">{raw}</p>;
      }
      const entries = Object.entries(parsed).filter(([, v]) => v !== null && v !== "" && v !== undefined);
      if (entries.length === 0) return null;
      return (
        <div className="mt-2 space-y-1">
          {entries.map(([key, value]) => (
            <div key={key} className="flex gap-2 text-sm">
              <span className="shrink-0 font-medium text-muted-foreground min-w-[120px]">
                {formatFieldLabel(key)}:
              </span>
              <span className="text-foreground">{String(value)}</span>
            </div>
          ))}
        </div>
      );
    } catch {
      return <p className="mt-2 text-sm text-foreground whitespace-pre-wrap">{raw}</p>;
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Legal Cases</h3>
        <Button size="sm" className="gap-2" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" />
          Add Case
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="p-4">
            <LegalCaseForm
              onSubmit={(d) => createMutation.mutate(d)}
              onCancel={() => setShowForm(false)}
              isLoading={createMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {!isLoading && cases.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No legal cases found.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading &&
        cases.map((c) => {
          const authorName =
            c.author_first_name && c.author_last_name
              ? `${c.author_first_name} ${c.author_last_name}`
              : null;
          const isEditing = editingCaseId === c.id;

          if (isEditing) {
            return (
              <Card key={c.id} className="border-primary/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Edit Legal Case</CardTitle>
                </CardHeader>
                <CardContent>
                  <LegalCaseForm
                    initial={c}
                    onSubmit={(d) =>
                      updateMutation.mutate({ id: c.id, ...d })
                    }
                    onCancel={() => setEditingCaseId(null)}
                    isLoading={updateMutation.isPending}
                  />
                </CardContent>
              </Card>
            );
          }

          return (
            <Card key={c.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-medium">
                      Legal Case #{c.id}
                    </CardTitle>
                    <CardDescription className="mt-0.5">
                      {new Date(c.created_at).toLocaleDateString()}
                      {authorName && ` | ${authorName}`}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingCaseId(c.id);
                        setShowForm(false);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm("Delete this legal case?")) {
                          deleteMutation.mutate(c.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {c.law_enforcement && (
                  <div className="text-xs text-muted-foreground">
                    {renderLawEnforcement(c.law_enforcement)}
                  </div>
                )}
                {renderCaseDetails(c.case_details)}
              </CardContent>
            </Card>
          );
        })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

interface LegalCaseFormProps {
  initial?: LegalCaseRecord;
  onSubmit: (data: { caseDetails?: string; lawEnforcement?: string }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function LegalCaseForm({
  initial,
  onSubmit,
  onCancel,
  isLoading,
}: LegalCaseFormProps) {
  // Try to parse JSON case_details into structured fields for editing
  const parsedJson = (() => {
    if (!initial?.case_details) return null;
    try {
      const parsed = JSON.parse(initial.case_details);
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) return parsed as Record<string, string>;
      return null;
    } catch { return null; }
  })();

  const [isStructured] = useState(!!parsedJson);
  const [structuredFields, setStructuredFields] = useState<Record<string, string>>(parsedJson ?? {});
  const [caseDetails, setCaseDetails] = useState(
    parsedJson ? "" : (initial?.case_details ?? ""),
  );
  const [lawEnforcement, setLawEnforcement] = useState(() => {
    const raw = initial?.law_enforcement ?? "";
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
        return Object.entries(parsed)
          .filter(([, v]) => v !== null && v !== "" && v !== undefined)
          .map(([key, value]) => {
            const label = key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
            if (Array.isArray(value)) return `${label}: ${value.join(", ")}`;
            return `${label}: ${String(value)}`;
          })
          .join("\n");
      }
      return raw;
    } catch {
      return raw;
    }
  });

  // Common structured fields for a legal case
  const structuredFieldDefs = [
    { key: "case_number", label: "Case Number" },
    { key: "case_type", label: "Case Type" },
    { key: "court_name", label: "Court" },
    { key: "judge_name", label: "Judge" },
    { key: "charges", label: "Charges" },
    { key: "filing_date", label: "Filing Date", type: "date" as const },
    { key: "hearing_date", label: "Hearing Date", type: "date" as const },
    { key: "next_hearing", label: "Next Hearing", type: "date" as const },
    { key: "offense_date", label: "Offense Date", type: "date" as const },
    { key: "arrest_date", label: "Arrest Date", type: "date" as const },
    { key: "status", label: "Status" },
    { key: "disposition", label: "Disposition" },
    { key: "notes", label: "Notes" },
  ];

  // Get all keys: structured field defs + any extra keys from parsed JSON
  const allKeys = isStructured
    ? [...new Set([...structuredFieldDefs.map((f) => f.key), ...Object.keys(structuredFields)])]
    : [];

  function getLabel(key: string): string {
    const def = structuredFieldDefs.find((f) => f.key === key);
    return def?.label ?? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function getInputType(key: string): string | undefined {
    const def = structuredFieldDefs.find((f) => f.key === key);
    return def && "type" in def ? (def as { type: string }).type : undefined;
  }

  /** Check if a key looks like a date field */
  function isDateKey(key: string): boolean {
    return /date|hearing|arrest|offense|filing/i.test(key) || getInputType(key) === "date";
  }

  function handleStructuredSubmit() {
    // Filter out empty values and build JSON
    const filtered = Object.fromEntries(
      Object.entries(structuredFields).filter(([, v]) => v && v.trim()),
    );
    const details = Object.keys(filtered).length > 0 ? JSON.stringify(filtered) : undefined;
    onSubmit({
      caseDetails: details,
      lawEnforcement: lawEnforcement || undefined,
    });
  }

  return (
    <div className="space-y-3">
      {isStructured ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Case Details</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {allKeys.map((key) => (
              <Input
                key={key}
                label={getLabel(key)}
                type={isDateKey(key) ? "date" : "text"}
                value={structuredFields[key] ?? ""}
                onChange={(e) =>
                  setStructuredFields((prev) => ({ ...prev, [key]: e.target.value }))
                }
                placeholder={isDateKey(key) ? undefined : getLabel(key)}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Case Details
          </label>
          <textarea
            value={caseDetails}
            onChange={(e) => setCaseDetails(e.target.value)}
            rows={4}
            placeholder="Enter case details..."
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      )}
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Law Enforcement</label>
        <textarea
          value={lawEnforcement}
          onChange={(e) => setLawEnforcement(e.target.value)}
          rows={2}
          placeholder="Agency, officer, report number..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="flex gap-3">
        <Button
          size="sm"
          onClick={() =>
            isStructured
              ? handleStructuredSubmit()
              : onSubmit({
                  caseDetails: caseDetails || undefined,
                  lawEnforcement: lawEnforcement || undefined,
                })
          }
          loading={isLoading}
        >
          {initial ? "Update Case" : "Create Case"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
